/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState, useEffect, lazy, Suspense } from "react";
import { TamaguiProvider, View, useMedia } from "@tamagui/core";
import { YStack, XStack } from "@tamagui/stacks";
import { Button } from "@tamagui/button";
import { H2 } from "@tamagui/text";
import { SizableText } from "@tamagui/text";
import { Progress } from "@tamagui/progress";
import tamaguiConfig from "./tamagui.config";
import { DevAuthGuard } from "./components/auth/DevAuthGuard";
import { AnalyticsProvider } from "./components/analytics/AnalyticsProvider";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import { ADHDErrorBoundary } from "./components/error-boundary/ADHDErrorBoundary";
import { FocusManagerProvider } from "./components/accessibility/FocusManager";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { FocusModeProvider } from "./contexts/FocusModeContext";
import { UsageTrackingProvider } from "./contexts/UsageTrackingContext";
import { AIUsageProvider } from "./contexts/AIUsageContext";
import { ToastProvider } from "./components/toast-notification";
import { SettingsProvider } from "./contexts/SettingsContext";
import { timeService } from "./lib/time-service";
import { LiveClientOptions } from "./types";
import "./lib/i18n";
import "./App.scss";
import "./styles/mobile-responsive.scss";
const Text = SizableText;
const Paragraph = SizableText;

// Lazy-loaded components for performance
const ADHDDashboard = lazy(() => import("./components/dashboard/ADHDDashboard"));
const TaskManagementV2 = lazy(() => import("./components/task-management/TaskManagementV2"));
const ADHDAssistantV2 = lazy(() => import("./components/adhd-assistant/ADHDAssistantV2"));
const SettingsV2 = lazy(() => import("./components/settings/SettingsV2"));
const MobileBottomNav = lazy(() => import("./components/navigation/MobileBottomNav"));
const LazyAdvancedAnalyticsDashboard = lazy(
  () => import("./components/analytics/AdvancedAnalyticsDashboard")
);

// SaaS Mode: API keys are managed by our backend proxy
// Users don't need to provide their own API keys

function App() {
  // this video reference is used for displaying the active stream, whether that is the webcam or screen capture
  // feel free to style as you see fit
  const videoRef = useRef<HTMLVideoElement>(null);
  // either the screen capture, the video or null, if null we hide it
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  // Initialize the time service on app startup
  useEffect(() => {
    timeService.initialize().catch(error => {
      console.warn("Failed to initialize time service:", error);
    });
  }, []);

  return (
    <TamaguiProvider config={tamaguiConfig}>
      <SettingsProvider>
        <FocusManagerProvider>
          <View flex={1} backgroundColor="$background">
            <ADHDErrorBoundary level="page">
              <DevAuthGuard>
                <AnalyticsProvider>
                  <AppContent
                    videoRef={videoRef}
                    videoStream={videoStream}
                    setVideoStream={setVideoStream}
                  />
                </AnalyticsProvider>
              </DevAuthGuard>
            </ADHDErrorBoundary>
          </View>
        </FocusManagerProvider>
      </SettingsProvider>
    </TamaguiProvider>
  );
}

// Navigation types
type AppView = "dashboard" | "tasks" | "focus" | "analytics" | "settings";
type FocusLevel = "minimal" | "focused" | "full";

function AppContent({
  videoRef,
  videoStream,
  setVideoStream,
}: {
  videoRef: React.RefObject<HTMLVideoElement>;
  videoStream: MediaStream | null;
  setVideoStream: (stream: MediaStream | null) => void;
}) {
  const [currentView, setCurrentView] = useState<AppView>("dashboard");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [focusLevel, setFocusLevel] = useState<FocusLevel>("full");
  const media = useMedia() as any; // Type fix for media queries

  // Mobile-first navigation configuration
  const navItems = [
    {
      id: "dashboard" as AppView,
      label: "Home",
      icon: "🏠",
      ariaLabel: "Navigate to home dashboard",
    },
    {
      id: "tasks" as AppView,
      label: "Tasks",
      icon: "✅",
      ariaLabel: "Navigate to task management",
      badge: 3,
    },
    {
      id: "focus" as AppView,
      label: "Focus",
      icon: "🎯",
      ariaLabel: "Navigate to focus mode",
    },
    {
      id: "analytics" as AppView,
      label: "Stats",
      icon: "📊",
      ariaLabel: "Navigate to analytics dashboard",
    },
  ];

  // Check URL for test mode
  const urlParams = new URLSearchParams(window.location.search);
  const testMode = urlParams.get("test") === "subscription";

  // Show test page if in test mode
  if (testMode) {
    return (
      <View flex={1} height="100vh">
        <View position="absolute" top="$2" right="$2" zIndex={1000}>
          <Button onPress={() => (window.location.href = window.location.pathname)}>
            Back to App
          </Button>
        </View>
        {/* <SubscriptionTestPage /> */}
        <View padding="$4">
          <YStack space="$4">
            <Paragraph>Subscription Test Page (Tamagui version coming soon)</Paragraph>
          </YStack>
        </View>
      </View>
    );
  }

  // Loading spinner component
  const LoadingView = () => (
    <View flex={1} alignItems="center" justifyContent="center" minHeight="100vh">
      <YStack space="$4" alignItems="center">
        <Progress value={60} width="$10" height="$1">
          <Progress.Indicator animation="bouncy" backgroundColor="$adhd_focus" />
        </Progress>
        <Text color="$gray10" fontSize="$3">
          Loading...
        </Text>
      </YStack>
    </View>
  );

  // Render content based on current view
  const renderContent = () => {
    switch (currentView) {
      case "dashboard":
        return (
          <Suspense fallback={<LoadingView />}>
            <ADHDDashboard
              onNavigateToTasks={() => setCurrentView("tasks")}
              onNavigateToFocus={() => setCurrentView("focus")}
              onNavigateToAnalytics={() => setCurrentView("analytics")}
              onNavigateToSettings={() => setCurrentView("settings")}
              onQuickAction={actionId => console.log("Quick action:", actionId)}
            />
          </Suspense>
        );

      case "tasks":
        return (
          <Suspense fallback={<LoadingView />}>
            <TaskManagementV2 />
          </Suspense>
        );

      case "focus":
        return (
          <Suspense fallback={<LoadingView />}>
            <ADHDAssistantV2
              onNavigateToSettings={() => setCurrentView("settings")}
              onNavigateToAnalytics={() => setCurrentView("analytics")}
            />
          </Suspense>
        );

      case "analytics":
        return (
          <Suspense fallback={<LoadingView />}>
            <LazyAdvancedAnalyticsDashboard />
          </Suspense>
        );

      case "settings":
        return (
          <Suspense fallback={<LoadingView />}>
            <SettingsV2 onNavigateBack={() => setCurrentView("dashboard")} />
          </Suspense>
        );

      default:
        return null;
    }
  };

  // Mobile-first header component
  const Header = () => {
    if (focusLevel === "minimal") return null;

    return (
      <View
        backgroundColor="$background"
        borderBottomWidth={1}
        borderColor="$borderColor"
        paddingHorizontal="$4"
        paddingVertical="$3"
        zIndex={10}
      >
        <XStack justifyContent="space-between" alignItems="center">
          <XStack space="$3" alignItems="center">
            <Text fontSize="$5" fontWeight="600" color="$color">
              Flowcus
            </Text>
            {focusLevel === "full" && (
              <Text fontSize="$2" color="$gray10">
                {currentView === "dashboard"
                  ? "Home"
                  : currentView.charAt(0).toUpperCase() + currentView.slice(1)}
              </Text>
            )}
          </XStack>

          {focusLevel === "full" && (
            <XStack space="$2">
              <Button
                size="$3"
                circular
                backgroundColor="transparent"
                borderWidth={0}
                onPress={() => setCurrentView("settings")}
              >
                <Text fontSize="$5">⚙️</Text>
              </Button>
            </XStack>
          )}
        </XStack>
      </View>
    );
  };

  // Desktop sidebar navigation
  const DesktopSidebar = () => {
    if (!media?.gtMd || focusLevel === "minimal") return null;

    return (
      <View
        width={240}
        backgroundColor="$backgroundHover"
        borderRightWidth={1}
        borderColor="$borderColor"
        padding="$4"
      >
        <YStack space="$2">
          <H2 fontSize="$6" marginBottom="$4">
            Flowcus
          </H2>

          {navItems.map(item => (
            <Button
              key={item.id}
              size="$4"
              backgroundColor={currentView === item.id ? "$adhd_focus" : "transparent"}
              borderWidth={0}
              justifyContent="flex-start"
              onPress={() => setCurrentView(item.id)}
            >
              <XStack space="$3" alignItems="center">
                <Text fontSize="$5">{item.icon}</Text>
                <Text color={currentView === item.id ? "$background" : "$color"} fontSize="$4">
                  {item.label}
                </Text>
                {item.badge && (
                  <View
                    backgroundColor="$adhd_urgent"
                    borderRadius="$10"
                    paddingHorizontal="$2"
                    paddingVertical="$1"
                  >
                    <Text fontSize="$1" color="$background">
                      {item.badge}
                    </Text>
                  </View>
                )}
              </XStack>
            </Button>
          ))}

          <View height="$4" />

          <Button
            size="$4"
            backgroundColor={currentView === "settings" ? "$adhd_focus" : "transparent"}
            borderWidth={0}
            justifyContent="flex-start"
            onPress={() => setCurrentView("settings")}
          >
            <XStack space="$3" alignItems="center">
              <Text fontSize="$5">⚙️</Text>
              <Text color={currentView === "settings" ? "$background" : "$color"} fontSize="$4">
                Settings
              </Text>
            </XStack>
          </Button>
        </YStack>
      </View>
    );
  };

  // Main layout structure
  const wrappedContent = (
    <View flex={1} height="100vh" backgroundColor="$background">
      {media?.gtMd ? (
        // Desktop layout with sidebar
        <XStack flex={1} height="100%">
          <DesktopSidebar />
          <YStack flex={1}>
            <Header />
            <View flex={1}>{renderContent()}</View>
          </YStack>
        </XStack>
      ) : (
        // Mobile layout with bottom navigation
        <YStack flex={1} height="100%">
          <Header />
          <View
            flex={1}
            paddingBottom={focusLevel === "minimal" ? "$0" : 100}
            style={{ overflow: "auto" }}
            position="relative"
          >
            {renderContent()}
          </View>

          {/* Mobile bottom navigation */}
          {(focusLevel === "focused" || focusLevel === "full") && currentView !== "settings" && (
            <Suspense fallback={null}>
              <MobileBottomNav
                activeView={currentView}
                onViewChange={view => setCurrentView(view as AppView)}
                items={navItems}
              />
            </Suspense>
          )}
        </YStack>
      )}

      {/* Hidden video element for future use */}
      <View
        tag="video"
        ref={videoRef}
        position="absolute"
        opacity={0}
        pointerEvents="none"
        // @ts-ignore - video props
        autoPlay
        playsInline
        muted
      />
    </View>
  );

  // SaaS Mode: Direct API access with ephemeral tokens
  const liveAPIOptions: LiveClientOptions = {
    apiKey: "dummy-key-for-ephemeral-tokens", // This will be replaced by ephemeral tokens
  };

  return (
    <SubscriptionProvider>
      <UsageTrackingProvider>
        <AIUsageProvider>
          <FocusModeProvider>
            <ToastProvider position="top-right">
              <LiveAPIProvider options={liveAPIOptions}>{wrappedContent}</LiveAPIProvider>
            </ToastProvider>
          </FocusModeProvider>
        </AIUsageProvider>
      </UsageTrackingProvider>
    </SubscriptionProvider>
  );
}

export default App;
